export * from "./types.js";
export * from "./design-temperatures.js";
export * from "./constructions.js";
export * from "./emitters.js";
export * from "./emitter-catalogue.js";
export * from "./underfloor-catalogue.js";
export * from "./heat-pump-catalogue.js";
export * from "./noise.js";
export * from "./hot-water.js";
export * from "./sizing.js";
export * from "./performance-estimate.js";
export * from "./plan-objects.js";
export * from "./pipe-stock-data.js";
export * from "./stock.js";
export * from "./stock-catalogue.js";
export * from "./heat-pump-datasheets.js";
export * from "./heat-pump-datasheet-data.js";

import { RADIATOR_CATALOGUES } from "./emitter-catalogue.js";
import { UNDERFLOOR_CATALOGUES } from "./underfloor-catalogue.js";
import { HEAT_PUMP_CATALOGUES } from "./heat-pump-catalogue.js";
import { MIS_3005_D_TABLE_1, MIS_3005_D_TABLE_2 } from "./design-temperatures.js";
import { MIS_3005_D_HOT_WATER } from "./hot-water.js";
import { NOISE_RULESETS } from "./noise.js";
import { MIS_3005_D_SIZING } from "./sizing.js";
import {
  RDSAP_AGE_BANDS,
  RDSAP_DOOR_U_VALUES,
  RDSAP_EXPOSED_FLOOR_U_VALUES,
  RDSAP_FLOOR_OVER_PARTIALLY_HEATED_U_VALUE,
  RDSAP_PARTY_WALL_U_VALUES,
  RDSAP_ROOF_U_VALUES_BY_AGE_BAND,
  RDSAP_ROOF_U_VALUES_BY_LOFT_INSULATION,
  RDSAP_WALL_U_VALUES,
  RDSAP_WINDOW_U_VALUES,
} from "./constructions.js";

/**
 * The complete set of rulesets, with a single version stamp recorded on every
 * calculation the engine produces. Bump RULES_BUNDLE_VERSION whenever any
 * contained ruleset changes, so a stored calculation can always be reproduced.
 */
// 2026.09.10: the MCS 020 a) ruleset gained its Step 4/Step 8 figures (1.2.0).
export const RULES_BUNDLE_VERSION = "2026.09.10";

export const ALL_RULESETS = {
  internalDesignTemperatures: MIS_3005_D_TABLE_1,
  externalDesignTemperatures: MIS_3005_D_TABLE_2,
  sizing: MIS_3005_D_SIZING,
  hotWater: MIS_3005_D_HOT_WATER,
  noise: NOISE_RULESETS,
} as const;

/**
 * Product catalogues, kept separate from ALL_RULESETS on purpose.
 *
 * RULES_BUNDLE_VERSION answers "which edition of the MCS scheme rules produced this
 * calculation". A manufacturer adding a radiator size is not an edition of the
 * scheme. Folding catalogues into the bundle would force a bundle bump — and with
 * it the re-verification of every stored calculation — every time a price list
 * landed, while telling an auditor nothing about which rules were applied.
 */
export const ALL_PRODUCT_CATALOGUES = {
  radiators: RADIATOR_CATALOGUES,
  underfloor: UNDERFLOOR_CATALOGUES,
  heatPumps: HEAT_PUMP_CATALOGUES,
} as const;

/**
 * The construction library's tables, kept out of ALL_RULESETS for the same
 * reason the catalogues are.
 *
 * These are RdSAP's published default U-values (constructions.ts explains why
 * RdSAP and not MCS: MIS 3005-D tabulates none). RdSAP is the method behind
 * domestic EPCs, not an edition of the MCS scheme, so a new RdSAP release is
 * not a new edition of the rules a calculation was run under, and folding it
 * into RULES_BUNDLE_VERSION would force every stored calculation to be
 * re-verified while telling an auditor nothing about which MCS rules applied.
 *
 * What DOES travel with a calculation is the individual figure: the surface
 * records the library entry's id, and that id names the table, the row and
 * the age band the number came from.
 */
export const ALL_CONSTRUCTION_RULESETS = {
  ageBands: RDSAP_AGE_BANDS,
  wallUValues: RDSAP_WALL_U_VALUES,
  partyWallUValues: RDSAP_PARTY_WALL_U_VALUES,
  roofUValuesByLoftInsulation: RDSAP_ROOF_U_VALUES_BY_LOFT_INSULATION,
  roofUValuesByAgeBand: RDSAP_ROOF_U_VALUES_BY_AGE_BAND,
  exposedFloorUValues: RDSAP_EXPOSED_FLOOR_U_VALUES,
  floorOverPartiallyHeatedUValue: RDSAP_FLOOR_OVER_PARTIALLY_HEATED_U_VALUE,
  windowUValues: RDSAP_WINDOW_U_VALUES,
  doorUValues: RDSAP_DOOR_U_VALUES,
} as const;
